import { useEffect, useState } from "react";
import { Calculator, Download, LoaderCircle } from "lucide-react";
import { downloadFile } from "./api";
import { MetricStrip, Pagination } from "./components";
import type { ShippingBatchResult } from "../server/shipping-workbooks";

type Rules = ShippingBatchResult["rules"];
const number = (value: number | undefined) => value === undefined ? "—" : value.toLocaleString("zh-CN", { maximumFractionDigits: 6 });

export function ShippingPricingPanel() {
  const [rules, setRules] = useState<Rules | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ShippingBatchResult | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/shipping-pricing/rules", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("运费规则暂时无法加载，请刷新页面重试。");
        return response.json() as Promise<Rules>;
      }).then(setRules).catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "规则加载失败。");
      });
    return () => controller.abort();
  }, []);

  async function preview() {
    if (!file) return;
    setBusy("preview"); setError(""); setResult(null);
    try {
      const body = new FormData(); body.append("file", file);
      const response = await fetch("/api/shipping-pricing/preview", { method: "POST", body });
      const payload = await response.json() as ShippingBatchResult & { error?: string };
      if (!response.ok) throw new Error(payload.error ?? "运费试算失败。");
      setResult(payload); setRules(payload.rules); setPage(1);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "运费试算失败。"); }
    finally { setBusy(""); }
  }

  async function download(kind: "template" | "result") {
    setBusy(kind); setError("");
    try {
      if (kind === "template") await downloadFile("/api/shipping-pricing/template", "运费试算模板.xlsx");
      else if (result && file) {
        const body = new FormData(); body.append("file", file); body.append("ruleHash", result.rules.hash);
        await downloadFile("/api/shipping-pricing/export", `运费试算-${result.rules.version}.xlsx`, { method: "POST", body });
      }
    } catch (cause) { setError(cause instanceof Error ? cause.message : "下载失败。"); }
    finally { setBusy(""); }
  }

  return <details className="workspace-band shipping-pricing-panel">
    <summary><Calculator size={18} aria-hidden="true" /><strong>毛重 / 抛重运费试算</strong><span>{rules ? `统一参考除数 ${rules.volumetricDivisor}` : "加载规则…"}</span></summary>
    <div className="shipping-pricing-content">
      <p>{rules?.routeLabel ?? "中国 / 中国香港菜鸟跨境直发"}。输入包装尺寸、毛重及站点售价，自动匹配运费。</p>
      {rules ? <p className="shipping-rule-note">长 × 宽 × 高（cm）÷ {rules.volumetricDivisor} ＝ 抛重（kg）；毛重小于 {rules.grossOnlyBelowKg * 1000} g 按毛重，其余取较大值。版本：{rules.version}</p> : null}
      <p className="shipping-rule-note">所有本地参考使用统一除数，实际费用优先采用官方计费重量。</p>
      <div className="shipping-import-actions">
        <div className="field-group"><label htmlFor="shipping-file">运费资料（CSV / XLSX）</label><input id="shipping-file" type="file" accept=".csv,.xlsx" disabled={Boolean(busy)} onChange={(event) => { setFile(event.target.files?.[0] ?? null); setResult(null); setError(""); }} /></div>
        <button className="button secondary" disabled={Boolean(busy)} onClick={() => void download("template")}><Download size={17} />下载填写模板</button>
        <button className="button secondary" disabled={!rules || !file || Boolean(busy)} onClick={() => void preview()}>{busy === "preview" ? <LoaderCircle size={17} className="spin" /> : <Calculator size={17} />}计算运费</button>
      </div>
      <p className="shipping-rule-note">最大 20 MB、10,000 行。此处按菜鸟静态费率表试算；实时官方费用及手工调价请使用上方“手工目标净收益 / Full 询价”。</p>
      {error ? <div className="form-error" role="alert">{error}</div> : null}
      {result ? <>
        <div role="status"><MetricStrip items={[{ label: "总行数", value: result.summary.total }, { label: "已试算", value: result.summary.quoted, tone: "positive" }, { label: "待补充 / 确认", value: result.summary.needsAttention }]} /></div>
        <div className="table-scroll"><table className="data-table"><thead><tr><th>SKU / 站点</th><th>毛重 kg</th><th>抛重 kg</th><th>计费 kg</th><th>重量档 kg</th><th>运费 USD</th><th>结果说明</th></tr></thead>
          <tbody>{result.rows.slice((page - 1) * 20, page * 20).map((row) => <tr key={row.rowNo}>
            <td><strong>{row.input.merchant_sku || row.input.platform_sku || row.input.item_id || `第 ${row.rowNo} 行`}</strong><small>{row.input.site_code}</small></td>
            <td>{number(row.quote?.grossWeightKg)}</td><td>{number(row.quote?.volumetricWeightKg)}</td><td>{number(row.quote?.billableWeightKg)}</td>
            <td>{row.quote?.matchedBands.join(" / ") || "—"}</td><td>{row.quote?.shippingUsd == null ? "待确认" : row.quote.shippingUsd.toFixed(2)}</td>
            <td className="shipping-result-note">{row.error || row.quote?.message}</td>
          </tr>)}</tbody></table></div>
        <Pagination page={page} pageSize={20} total={result.summary.total} onChange={setPage} />
        <button className="button secondary" disabled={Boolean(busy)} onClick={() => void download("result")}>{busy === "result" ? <LoaderCircle className="spin" size={17} /> : <Download size={17} />}下载全部试算结果</button>
      </> : null}
    </div>
  </details>;
}
